import type { TFunction } from "i18next";
import {
  AccountingTemplateCategoryEnum,
  AttrTypes,
  B2bGateSupplierOrderStatusEnum,
  BankAccountType,
  BankSepaEnum,
  BankTransactionStatusEnum,
  CalculateTypeEnum,
  CalculationType,
  DocumentTypes,
  DownloadCustomersOptionEnum,
  EmailContactType,
  EmailTemplateTypeEnum,
  PhoneContactType,
  PriceListItemCalculateType,
  ProductStatusEnum,
  ProductStockFlagEnum,
  ProductType,
  SupplierOrderStatusEnum,
  UnitTypeEnum,
  VariantStatusEnum,
  MarketplaceTypeEnum,
} from "@exonn/js-sdk";
import { ResourceEnum } from "src/constants/constants.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { OrderStatusEnum } from "src/admin/modules/order/constants.ts";
import { PriceListStatus } from "src/admin/forms/price-list/shared/types.ts";
import { STOCK_MOVE_OPERATIONS_ENUM } from "src/admin/modules/stock-move/constants.ts";
import { AccountingAccountCategoryEnum } from "@exonn/js-sdk/src/namespaces/accounting-account";
import { AccountingAccountTypeEnum } from "src/admin/modules/accounting-account/constants.ts";

const enumValues = <T extends Record<string, string>>(e: T) =>
  Object.values(e) as Array<T[keyof T]>;

const ENUM_VALUE_MAP: Record<string, Record<string, string[]>> = {
  [ResourceEnum.DeliveryService]: {
    calculate_type_1: enumValues(CalculateTypeEnum),
    calculate_type_2: enumValues(CalculateTypeEnum),
    calculate_type: enumValues(CalculateTypeEnum),
  },
  [ResourceEnum.Variant]: {
    status: enumValues(VariantStatusEnum),
    stock_flag: enumValues(ProductStockFlagEnum),
  },
  [ResourceEnum.AccountingAccount]: {
    category: enumValues(AccountingAccountCategoryEnum),
    type: enumValues(AccountingAccountTypeEnum),
  },
  [ResourceEnum.AccountingTemplate]: {
    category: enumValues(AccountingTemplateCategoryEnum),
  },
  [ResourceEnum.StockMove]: {
    movement_type: enumValues(STOCK_MOVE_OPERATIONS_ENUM),
  },
  [ResourceEnum.Product]: {
    status: enumValues(ProductStatusEnum),
    type: enumValues(ProductType),
    stock_flag: enumValues(ProductStockFlagEnum),
  },
  [ResourceEnum.Attribute]: {
    attribute_type: enumValues(AttrTypes),
  },
  [ResourceEnum.BankAccount]: {
    account_type: enumValues(BankAccountType),
  },
  [ResourceEnum.SupplierOrder]: {
    status: enumValues(SupplierOrderStatusEnum),
  },
  [ResourceEnum.Order]: {
    status: enumValues(OrderStatusEnum),
  },
  [ResourceEnum.B2bGateOrder]: {
    status: enumValues(B2bGateSupplierOrderStatusEnum),
  },
  [ResourceEnum.PriceList]: {
    status: enumValues(PriceListStatus),
  },
  [ResourceEnum.PriceListItem]: {
    status: enumValues(PriceListItemCalculateType),
    calculate_type: enumValues(CalculationType),
  },
  [ResourceEnum.EmailTemplate]: {
    email_type: enumValues(EmailTemplateTypeEnum),
  },
  [ResourceEnum.Document]: {
    document_type: enumValues(DocumentTypes),
  },
  [ResourceEnum.Unit]: {
    type: enumValues(UnitTypeEnum),
  },
  [ResourceEnum.BankSepa]: {
    type: enumValues(BankSepaEnum),
  },
  [ResourceEnum.Contact]: {
    type: [...enumValues(PhoneContactType), ...enumValues(EmailContactType)],
  },
  [ResourceEnum.MarketingCompany]: {
    download_option: enumValues(DownloadCustomersOptionEnum),
    downloaded_by: enumValues(DownloadCustomersOptionEnum),
  },
  [ResourceEnum.BankTransaction]: {
    status: enumValues(BankTransactionStatusEnum),
  },
  [ResourceEnum.Marketplace]: {
    marketplace_type: enumValues(MarketplaceTypeEnum),
  },
};

export type GetResourceFieldOptionsParams<Value = string> = {
  resource: string;
  field: string;
  t: TFunction;
  ns?: string;
  values?: Value[];
};

export type Options<Value = string> = {
  value: Value;
  label: string;
}[];

// todo: typesafe
export const getResourceFieldOptions = <Value = string>({
  resource,
  field,
  t,
  values: explicitValues,
}: GetResourceFieldOptionsParams<Value>): Options<Value> => {
  const values =
    explicitValues ?? (ENUM_VALUE_MAP[resource]?.[field] as Value[]);

  if (!values) {
    throw new Error(
      `options for [${resource}] resource field [${field}] not found`,
    );
  }

  return generateOptionsFromValuesList<Value>({
    resource,
    t,
    field,
    values,
  });
};

const generateOptionsFromValuesList = <Value = string>({
  resource,
  field,
  ns,
  t,
  values,
  getKey,
}: {
  resource: string;
  field: string;
  ns?: string;
  values: Value[];
  t: TFunction;
  getKey?: (params: { field: string; value: Value }) => string;
}): Options<Value> => {
  return values.map((value) => ({
    value,
    label: getResourceFieldOptionLabelTranslation<Value>({
      ns,
      t,
      optionValue: value,
      field,
      resource,
      getKey,
    }),
  }));
};

type OptionLabelValueParams = {
  type: "option_label";
  value: string;
};
export const getResourceFieldTranslation = ({
  resource,
  type,
  field,
  t,
  ...restParams
}: {
  resource: string;
  field: string;
  t: TFunction;
} & OptionLabelValueParams) => {
  if (type === "option_label") {
    return getResourceFieldOptionLabelTranslation({
      resource,
      field: field,
      t,
      optionValue: restParams.value,
    });
  }
  throw new Error("not implemented");
};

const getOptionLabelKeyShortPath = <Value = string>({
  value,
  field,
}: {
  value: Value;
  field: string;
}) => `field.${field}.options.${value}`;

const getOptionLabelKey = <Value = string>({
  value,
  field,
}: {
  value: Value;
  field: string;
}) => `field.${field}.options.${value}.label`;

export const getResourceFieldOptionLabelTranslation = <Value = string>({
  t,
  resource,
  field,
  ns,
  optionValue,
  getKey = getOptionLabelKey<Value>,
  getDefaultKey = getOptionLabelKeyShortPath<Value>,
}: {
  resource: string;
  field: string;
  ns?: string;
  optionValue: Value;
  t: TFunction;
  getKey?: (params: { field: string; value: Value }) => string;
  getDefaultKey?: (params: { field: string; value: Value }) => string;
}) => {
  const namespace = ns ?? getResourceI18nNamespace(resource);
  const valueFromShortPath = t(
    getDefaultKey({
      field,
      value: optionValue,
    }),
    {
      defaultValue: optionValue,
      ns: namespace,
    },
  );
  return t(
    getKey({
      field,
      value: optionValue,
    }),
    {
      ns: namespace,
      defaultValue: valueFromShortPath,
    },
  );
};
